import React, { memo } from 'react';
import { format } from 'date-fns';

import { areEqual } from 'Utils/equalityChecks';
import { TimelineProjectRow, TimelineBar } from 'Containers/SerializedEquipment/types';

import classes from './equipmentGantt.module.css';

interface Props {
  projects: TimelineProjectRow[];
  // Injectable reference for "now" (open bars extend to it). Defaults to the current time;
  // tests pass a fixed value for deterministic geometry.
  now?: Date;
  // When provided, bars are clickable — used to open that placement's record for editing.
  onBarClick?: (bar: TimelineBar) => void;
}

// Number of evenly-spaced ticks on the bottom date axis (also the gridline count).
const TICK_COUNT = 6;

function toMs(value: string | null, fallback: number): number {
  if (!value) return fallback;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? fallback : time;
}

function fmt(ms: number): string {
  return format(new Date(ms), 'MMM d, yyyy');
}

// Compact form for per-bar captions / axis ticks.
function fmtShort(ms: number): string {
  return format(new Date(ms), 'MMM d');
}

function EquipmentGantt({ projects, now, onBarClick }: Props) {
  const nowMs = (now ?? new Date()).getTime();
  const allBars = projects.flatMap((row) => row.bars);

  if (allBars.length === 0) {
    return (
      <p className={classes.empty} data-testid="gantt-empty">
        No equipment movement yet.
      </p>
    );
  }

  const axisMin = Math.min(...allBars.map((bar) => toMs(bar.date_in, nowMs)));
  const axisMax = Math.max(...allBars.map((bar) => toMs(bar.date_out, nowMs)), nowMs);
  const range = Math.max(axisMax - axisMin, 1);

  // Evenly-spaced tick timestamps across the whole range; positions are i/(TICK_COUNT-1).
  const ticks = Array.from({ length: TICK_COUNT }, (_, i) => axisMin + (range * i) / (TICK_COUNT - 1));

  const geometry = (bar: TimelineBar) => {
    const start = toMs(bar.date_in, axisMin);
    const end = toMs(bar.date_out, nowMs);
    return {
      left: ((start - axisMin) / range) * 100,
      width: Math.max(((end - start) / range) * 100, 2),
    };
  };

  return (
    <div className={classes.gantt} data-testid="equipment-gantt">
      {projects.map((row) => (
        <div key={row.project?.id ?? 'unknown'} className={classes.row} data-testid="gantt-row">
          <div className={classes.label}>
            <span className={classes.projectUid}>{row.project?.uid ?? 'Unknown project'}</span>
            {row.project?.address && <span className={classes.address}>{row.project.address}</span>}
          </div>
          <div className={classes.track}>
            {/* gridlines aligned with the bottom axis ticks */}
            <div className={classes.gridlines} aria-hidden="true">
              {ticks.map((tick, i) => (
                <span
                  key={tick}
                  className={classes.gridline}
                  style={{ left: `${(i / (TICK_COUNT - 1)) * 100}%` }}
                />
              ))}
            </div>
            {row.bars.map((bar) => {
              const { left, width } = geometry(bar);
              const startMs = toMs(bar.date_in, axisMin);
              const endMs = toMs(bar.date_out, nowMs);
              const dateRange = bar.date_out ? `${fmt(startMs)} – ${fmt(endMs)}` : `${fmt(startMs)} – Present`;
              const captionRange = bar.date_out ? `${fmtShort(startMs)} – ${fmtShort(endMs)}` : `${fmtShort(startMs)} – Now`;
              // Identify the specific serialized unit, not just its type: name + serial/asset tag.
              const unitId = bar.asset.serial_number || bar.asset.asset_tag;
              const barText = unitId ? `${bar.asset.name} · ${unitId}` : bar.asset.name;
              // Room adds location context to the caption under the bar.
              const caption = bar.room?.name ? `${bar.room.name} · ${captionRange}` : captionRange;
              const barTitle = [bar.asset.name, unitId, bar.room?.name, dateRange].filter(Boolean).join(' · ');
              return (
                <div key={bar.placement_id} className={classes.barWrap} style={{ left: `${left}%`, width: `${width}%` }}>
                  {onBarClick ? (
                    <button
                      type="button"
                      className={`${classes.bar} ${bar.is_open ? classes.barOpen : ''} ${classes.clickable}`}
                      title={`${barTitle} · click to edit`}
                      data-testid="gantt-bar"
                      onClick={() => onBarClick(bar)}
                    >
                      <span className={classes.barLabel}>{barText}</span>
                      {bar.is_open && <span className={classes.currentBadge}>Current</span>}
                    </button>
                  ) : (
                    <div
                      className={`${classes.bar} ${bar.is_open ? classes.barOpen : ''}`}
                      title={barTitle}
                      data-testid="gantt-bar"
                    >
                      <span className={classes.barLabel}>{barText}</span>
                      {bar.is_open && <span className={classes.currentBadge}>Current</span>}
                    </div>
                  )}
                  <span className={classes.barCaption} data-testid="gantt-bar-dates">
                    {caption}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Bottom date axis, aligned under the tracks (offset by the label column). */}
      <div className={classes.axis}>
        <div className={classes.axisSpacer} aria-hidden="true" />
        <div className={classes.axisTicks}>
          {ticks.map((tick, i) => {
            const pct = (i / (TICK_COUNT - 1)) * 100;
            let edge = '';
            if (i === 0) edge = classes.tickStart;
            else if (i === TICK_COUNT - 1) edge = classes.tickEnd;
            return (
              <span key={tick} className={`${classes.tick} ${edge}`} style={{ left: `${pct}%` }}>
                {fmtShort(tick)}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const EquipmentGanttMemo = memo(EquipmentGantt, areEqual);

export { EquipmentGanttMemo as EquipmentGantt };
